import { useEffect, useState } from 'react';
import { selectUser } from '../redux/reducers/userSlice';
import { useAppSelector } from '../redux/hooks';
import { useLazyLoadQuery } from 'react-relay';
import { sitesUsersQuery } from '../graphQLOperations/Sites';
import { Grade } from '../models/Grade';
import { Site } from '../models/Site';
import { Role } from '../models/Role';
import { Roles } from '../models/User';
import { SitesUsersQuery } from '../graphQLOperations/__generated__/SitesUsersQuery.graphql';

export interface StudentOrTeacher {
  id: string;
  name: string;
  site: string;
  grade: string;
  role: string;
  classes: string;
  email: string;
}

export interface CompleteUser {
  id: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  publicUserId: string;
  grade: Grade;
  site: Site[];
  role: Role[];
  activeUserClassesLength?: number;
  externalSync?: boolean;
  currentRoadmap?: { id: string; name: string };
}

interface Props {
  roleIds: string[];
  refetchKey?: number;
}

const useGetUsersBySite = ({ roleIds, refetchKey }: Props) => {
  const [users, setUsers] = useState<StudentOrTeacher[]>([]);
  const [completeUsers, setCompleteUsers] = useState<CompleteUser[]>([]);
  const [loading, setLoading] = useState(true);
  const userRedux = useAppSelector(selectUser);
  const [dataToFilter, setDataToFilter] = useState<{
    grades: string[];
    sites: string[];
    classes: string[];
    roles: string[];
  }>({
    grades: [],
    sites: [],
    classes: [],
    roles: [],
  });

  const usersData = useLazyLoadQuery<SitesUsersQuery>(
    sitesUsersQuery,
    {
      sitesFilter: {
        userId: `UserNode:${userRedux?.id}`,
      },
      usersFilter: {
        roleIds,
      },
    },
    { fetchKey: refetchKey, fetchPolicy: 'store-and-network' },
  );

  useEffect(() => {
    if (usersData) {
      const usersArray: StudentOrTeacher[] = [];
      const completeUsersArray: CompleteUser[] = [];
      const gradesFilter: string[] = [];
      const sitesFilter: string[] = [];
      const classesFilter: string[] = [];
      const rolesFilter: string[] = [];
      const data = usersData?.sites?.nodes?.map(stu => stu);
      data?.forEach(site => {
        site?.users?.forEach(user => {
          const usersToAppend = {
            id: user?.id ?? '',
            name: `${user?.firstName || ''} ${user?.lastName || ''}`,
            grade:
              user?.gradeLevels
                ?.filter(site => site?.isActive)
                ?.map(grade => grade?.name)
                ?.join(', ') ?? '',
            classes: user?.classes?.map(cl => cl?.name)?.join(', ') ?? '',
            site:
              user?.sites
                ?.filter(site => site?.isActive)
                .map(site => site?.name)
                ?.join(', ') ?? '',
            role: user?.roles?.map(role => role?.name)?.join(', ') ?? '',
            email: user?.email ?? '',
          };
          const completeUsersToAppend = {
            id: user?.id || '',
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            emailAddress: user?.email || '',
            publicUserId: user?.publicUserId || '',
            grade: {
              id: user?.gradeLevels?.filter(site => site?.isActive)?.[0]?.id || '',
              name: user?.gradeLevels?.filter(site => site?.isActive)?.[0]?.name || '',
              order: user?.gradeLevels?.filter(site => site?.isActive)?.[0]?.order || -1,
            },
            site: user?.sites?.length
              ? user?.sites?.map(site => ({
                  id: site?.id || '',
                  name: site?.name || '',
                }))
              : [],
            role: user?.roles?.length
              ? user?.roles?.map(role => ({
                  id: role?.id || '',
                  name: (role?.name as Roles) || '',
                }))
              : [],
          };
          const gradesFilterToAppend =
            (user?.gradeLevels?.map(grade => grade?.name)?.flat() as unknown as string[]) ?? [];
          const sitesFilterToAppend = (user?.sites?.map(site => site?.name)?.flat() as unknown as string[]) ?? [];
          const classesFilterToAppend = (user?.classes?.map(cl => cl?.name)?.flat() as unknown as string[]) ?? [];
          const rolesFilterToAppend = (user?.roles?.map(role => role?.name)?.flat() as unknown as string[]) ?? [];
          if (!usersArray.find(u => u.id === usersToAppend.id)) {
            usersArray.push(usersToAppend);
          }
          if (!completeUsersArray.find(u => u.id === completeUsersToAppend.id)) {
            completeUsersArray.push(completeUsersToAppend);
          }

          gradesFilter.push(...gradesFilterToAppend);
          sitesFilter.push(...sitesFilterToAppend);
          classesFilter.push(...classesFilterToAppend);
          rolesFilter.push(...rolesFilterToAppend);
        });
      });
      setUsers(usersArray);
      setCompleteUsers(completeUsersArray);
      setDataToFilter({
        grades: gradesFilter,
        sites: sitesFilter,
        classes: classesFilter,
        roles: rolesFilter,
      });
      setLoading(false);
    }
  }, [usersData]);

  return { users, dataToFilter, completeUsers, loading };
};

export default useGetUsersBySite;
